/**
 * Global Command-Palette — ⌘K / Ctrl+K öffnet einen Modal-Overlay mit
 * Fuzzy-Search über Pages, Familien (live via /groups?q=) und Saved
 * Pricing-Szenarien.
 *
 * Mount einmal in App.tsx innerhalb des Routers.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../lib/api'
import type { ApiResponse, ProductGroupSummary } from '../types'
import { usePricingScenarios } from '../lib/pricingScenarios'

interface CommandResult {
  id: string
  kind: 'page' | 'family' | 'scenario'
  label: string
  sublabel?: string
  navigate: () => void
}

const NAV_PAGES: { label: string; path: string; kbd?: string }[] = [
  { label: 'Dashboard · Kacheln', path: '/tiles' },
  { label: 'Dashboard · Grafik', path: '/chart' },
  { label: 'Dashboard · GuV', path: '/guv' },
  { label: 'Marktplätze', path: '/channels' },
  { label: 'Produkte', path: '/products' },
  { label: 'Top-Profit-Familien', path: '/top-profit' },
  { label: 'Daten-Qualität', path: '/data-quality' },
  { label: 'Daten-Qualität · EK-Coverage', path: '/data-quality/eks' },
  { label: 'Pricing Kalkulator', path: '/pricing' },
  { label: 'Was-wäre-wenn · Simulator', path: '/simulate' },
  { label: 'Was-wäre-wenn · Vergleich', path: '/scenarios/compare' },
  { label: 'Was-wäre-wenn · Tornado', path: '/scenarios/tornado' },
  { label: 'Retouren-Strategie', path: '/returns-what-if' },
  { label: 'Sourcing', path: '/sourcing' },
  { label: 'Import-Area', path: '/imports' },
  { label: 'Einstellungen · API-Keys', path: '/settings' },
  { label: 'Einstellungen · Operating Costs', path: '/settings/operating-costs' },
  { label: 'Einstellungen · Lieferanten', path: '/settings/suppliers' },
  { label: 'Einstellungen · Konstanten', path: '/settings/constants' },
]

/** Mini-Fuzzy: case-insensitive substring + word-start Boost. */
function scoreMatch(haystack: string, needle: string): number {
  if (!needle) return 1
  const h = haystack.toLowerCase()
  const n = needle.toLowerCase()
  if (h === n) return 1000
  if (h.startsWith(n)) return 500
  // word-boundary match
  if (h.includes(` ${n}`) || h.includes(`-${n}`) || h.includes(`·${n}`) || h.includes(`/${n}`)) {
    return 200
  }
  if (h.includes(n)) return 50
  // multi-word: alle Tokens müssen irgendwo drinstecken
  const tokens = n.split(/\s+/).filter(Boolean)
  if (tokens.length > 1 && tokens.every((t) => h.includes(t))) return 30
  return 0
}

function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export function CommandPalette() {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const [families, setFamilies] = useState<ProductGroupSummary[]>([])
  const [scenarios] = usePricingScenarios()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const debouncedQuery = useDebounced(query, 150)

  /* Open/close per ⌘K (Cmd or Ctrl + K) */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
        return
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault()
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  /* Reset state when opening, focus the input */
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelected(0)
      // Defer focus so the modal is in the DOM
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  /* Familien live laden, sobald geöffnet oder Query ändert */
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const url = debouncedQuery
      ? `/groups?q=${encodeURIComponent(debouncedQuery)}&limit=30`
      : `/groups?limit=15`
    apiClient
      .get<ApiResponse<ProductGroupSummary[]>>(url)
      .then((res) => {
        if (cancelled) return
        if (res.data.success && res.data.data) setFamilies(res.data.data)
      })
      .catch(() => {
        /* silent — Familien-Lookup ist best-effort */
      })
    return () => {
      cancelled = true
    }
  }, [open, debouncedQuery])

  /* Result-Liste bauen mit Scoring */
  const results: CommandResult[] = useMemo(() => {
    const out: CommandResult[] = []

    // 1) Pages
    for (const p of NAV_PAGES) {
      const score = scoreMatch(p.label, query)
      if (score > 0) {
        out.push({
          id: `page-${p.path}`,
          kind: 'page',
          label: p.label,
          sublabel: p.path,
          navigate: () => nav(p.path),
        })
      }
    }

    // 2) Saved Scenarios
    for (const s of scenarios) {
      const composite = `${s.name} ${s.data.parent_sku ?? ''}`
      const score = scoreMatch(composite, query)
      if (score > 0) {
        const dest = `/pricing?scenario=${encodeURIComponent(s.id)}`
        out.push({
          id: `scenario-${s.id}`,
          kind: 'scenario',
          label: s.name,
          sublabel: `Pricing-Szenario · ${new Date(s.created_at).toLocaleDateString('de-DE')}`,
          navigate: () => nav(dest),
        })
      }
    }

    // 3) Familien (server-side already pre-filtered, but re-score for ranking)
    for (const f of families) {
      const score = scoreMatch(`${f.parent_sku} ${f.name}`, query)
      if (score > 0 || !query) {
        out.push({
          id: `family-${f.parent_sku}`,
          kind: 'family',
          label: f.parent_sku,
          sublabel: f.name,
          navigate: () => nav(`/simulate?parent_sku=${encodeURIComponent(f.parent_sku)}`),
        })
      }
    }

    return out
  }, [query, scenarios, families, nav])

  /* Keep selected index in bounds when results change */
  useEffect(() => {
    if (selected >= results.length) setSelected(0)
  }, [results.length, selected])

  const choose = useCallback(
    (r: CommandResult) => {
      r.navigate()
      setOpen(false)
    },
    [],
  )

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelected((i) => (results.length > 0 ? (i + 1) % results.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelected((i) =>
        results.length > 0 ? (i - 1 + results.length) % results.length : 0,
      )
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const r = results[selected]
      if (r) choose(r)
    }
  }

  if (!open) return null

  /* Sektionen für die Anzeige */
  const sections: { kind: CommandResult['kind']; title: string; items: CommandResult[] }[] = [
    { kind: 'page', title: 'Pages', items: results.filter((r) => r.kind === 'page') },
    { kind: 'scenario', title: 'Saved Szenarien', items: results.filter((r) => r.kind === 'scenario') },
    { kind: 'family', title: 'Familien', items: results.filter((r) => r.kind === 'family') },
  ]

  return (
    <div
      className="cmdk-backdrop"
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        zIndex: 80,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '14vh 16px 0',
      }}
    >
      <div
        className="cmdk-panel sk-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Globale Suche"
        style={{
          width: '100%',
          maxWidth: 640,
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 14 }}>
            ⌕
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelected(0)
            }}
            onKeyDown={onInputKeyDown}
            placeholder="Suche · Page, Familie, Szenario…"
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              background: 'transparent',
              fontSize: 16,
              fontFamily: 'var(--sketch-font)',
              color: 'var(--ink)',
            }}
          />
          <kbd
            className="mono"
            style={{
              fontSize: 10,
              padding: '2px 6px',
              border: '1px solid var(--line)',
              borderRadius: 4,
              color: 'var(--ink-3)',
              letterSpacing: '0.06em',
            }}
          >
            ESC
          </kbd>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '6px 0' }}>
          {results.length === 0 ? (
            <div
              className="mono"
              style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}
            >
              Keine Treffer für „{query}"
            </div>
          ) : (
            (() => {
              let runningIndex = 0
              return sections.map((section) => {
                if (section.items.length === 0) return null
                return (
                  <div key={section.kind} style={{ marginBottom: 4 }}>
                    <div
                      className="mono"
                      style={{
                        padding: '8px 16px 4px',
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      {section.title}
                    </div>
                    {section.items.map((r) => {
                      const globalIndex = runningIndex++
                      const isSelected = globalIndex === selected
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onMouseEnter={() => setSelected(globalIndex)}
                          onClick={() => choose(r)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            width: '100%',
                            padding: '8px 16px',
                            background: isSelected ? 'var(--surface-2)' : 'transparent',
                            border: 0,
                            borderLeft: isSelected
                              ? '2px solid var(--accent)'
                              : '2px solid transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            font: 'inherit',
                            color: 'var(--ink)',
                          }}
                        >
                          <span
                            className="mono"
                            style={{
                              fontSize: 9,
                              padding: '2px 6px',
                              borderRadius: 3,
                              background: 'var(--surface-2)',
                              color: 'var(--ink-3)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              fontWeight: 600,
                              minWidth: 56,
                              textAlign: 'center',
                              flexShrink: 0,
                              border: '1px solid var(--line)',
                            }}
                          >
                            {r.kind === 'page'
                              ? 'Page'
                              : r.kind === 'scenario'
                                ? 'Szenario'
                                : 'Familie'}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{r.label}</div>
                            {r.sublabel && (
                              <div
                                className="mono"
                                style={{
                                  fontSize: 10.5,
                                  color: 'var(--ink-3)',
                                  marginTop: 2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {r.sublabel}
                              </div>
                            )}
                          </div>
                          {isSelected && (
                            <span
                              className="mono"
                              style={{ fontSize: 11, color: 'var(--ink-3)' }}
                            >
                              ↵
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )
              })
            })()
          )}
        </div>

        <div
          className="mono"
          style={{
            display: 'flex',
            gap: 14,
            padding: '8px 16px',
            borderTop: '1px solid var(--line)',
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '0.04em',
          }}
        >
          <span>↑↓ navigieren</span>
          <span>↵ wählen</span>
          <span>esc schließen</span>
          <span style={{ marginLeft: 'auto' }}>{results.length} Treffer</span>
        </div>
      </div>
    </div>
  )
}
